import { type Token, type Tokens, lexer } from 'marked';

/**
 * An emphasis or code marker at the start of a word that nothing after it closes, which marked keeps
 * as literal text; markdown cut short ends with one.
 */
const UNCLOSED_MARKER = /(^|\s)(\*{1,3}|`)(?=\S)(?![\s\S]*\2)/;

/**
 * The readable text of agent markdown, as marked tokenizes it: emphasis, code spans, links and list
 * items keep their text, markup and HTML are dropped, markers that nothing closes are dropped, and
 * whitespace collapses to single spaces. For places that show a reply as one line of plain text, such
 * as a conversation preview.
 */
export function markdownToText(markdown: string): string {
  return withoutUnclosedMarkers(blocksText(lexer(markdown)))
    .replace(/\s+/g, ' ')
    .trim();
}

function withoutUnclosedMarkers(text: string): string {
  let result = text;
  let previous: string;
  do {
    previous = result;
    result = result.replace(UNCLOSED_MARKER, '$1');
  } while (result !== previous);
  return result;
}

/** Text of block tokens, which are separated by a space. */
function blocksText(tokens: readonly Token[]): string {
  return tokens.map(tokenText).join(' ');
}

/** Text of inline tokens, which run on without separators. */
function inlineText(tokens: readonly Token[]): string {
  return tokens.map(tokenText).join('');
}

function tokenText(token: Token): string {
  switch (token.type) {
    case 'list':
      return blocksText((token as Tokens.List).items);
    case 'list_item':
    case 'blockquote':
      return blocksText((token as Tokens.ListItem | Tokens.Blockquote).tokens);
    case 'table': {
      const table = token as Tokens.Table;
      return [...table.header, ...table.rows.flat()]
        .map((cell) => inlineText(cell.tokens))
        .join(' ');
    }
    case 'space':
    case 'hr':
    case 'br':
      return ' ';
    case 'html':
      return '';
    default:
      if ('tokens' in token && token.tokens?.length)
        return inlineText(token.tokens);
      return 'text' in token && typeof token.text === 'string'
        ? token.text
        : '';
  }
}
